import {
  ComponentRef,
  computed,
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { TooltipComponent } from '../components/tooltip/tooltip';
import { TooltipService } from '../services/tooltip.service';
import { SimpleTooltip } from '../components/simple-tooltip/simple-tooltip';

@Directive({
  selector: '[uiTooltip]',
  host: {
    '[attr.aria-describedby]': 'ariaDescribedBy()',
  },
})
export class TooltipDirective implements OnDestroy {
  private tooltipService = inject(TooltipService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);

  /**
   * Use this input if you have direct access to the target PopoverComponent.
   */
  uiTooltip = input<TooltipComponent | string | null>(null);
  /**
   * Use this input if you want to manually set a unique anchor name that you can reference from anywhere in the document.
   */
  anchorName = input<`--${string}`>(`--${crypto.randomUUID()}`);

  private simpleTooltipId = `ui-tooltip-${crypto.randomUUID()}`;
  private simpleTooltipRef = signal<ComponentRef<SimpleTooltip> | null>(null);

  ariaDescribedBy = computed(() => {
    const popover = this.uiTooltip();
    if (typeof popover === 'string') {
      return this.simpleTooltipId;
    } else if (popover instanceof TooltipComponent) {
      return popover.id();
    }
    return null;
  });

  private tooltipElement = computed(() => {
    const anchor = this.uiTooltip();
    if (anchor instanceof TooltipComponent) {
      return anchor;
    } else if (typeof anchor === 'string') {
      return this.simpleTooltipRef()?.instance;
    }
    return null;
  });

  constructor() {
    effect(() => {
      const popover = this.uiTooltip();
      const element = this.elementRef.nativeElement;
      const anchorName = this.anchorName();

      element.style.setProperty('anchor-name', anchorName);

      if (popover instanceof TooltipComponent) {
        popover.positionAnchor.set(anchorName);
        this.destroySimpleTooltip();
      } else if (typeof popover === 'string') {
        const ref = untracked(() => this.simpleTooltipRef());
        if (ref) {
          ref.setInput('text', popover);
          ref.setInput('anchorName', anchorName);
        }
      } else {
        this.destroySimpleTooltip();
      }
    });
  }

  private destroySimpleTooltip() {
    const ref = untracked(() => this.simpleTooltipRef());
    if (ref) {
      ref.destroy();
      this.simpleTooltipRef.set(null);
    }
  }

  ngOnDestroy() {
    this.destroySimpleTooltip();
  }

  @HostListener('mouseenter')
  @HostListener('focus')
  showTooltip() {
    const popover = this.uiTooltip();
    if (typeof popover === 'string') {
      let ref = this.simpleTooltipRef();
      if (!ref) {
        ref = this.tooltipService.create(popover, this.anchorName(), this.simpleTooltipId);
        this.simpleTooltipRef.set(ref);
      }
      ref.instance.show();
    } else if (popover instanceof TooltipComponent) {
      popover.show();
    }
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  hideTooltip() {
    const el = this.tooltipElement();
    if (el) {
      el.hide();
    }
  }
}
